<template>
  <section>
    <h3 class="my-2 font-bold">{{ accreditation?.name }}</h3>
    <div class="mb-6 flex items-center">
      <div class="flex-1 space-x-4">
        <input
          :id="'Yes' + accreditation.id"
          v-model="hasAccreditation"
          type="radio"
          :name="'hasAccreditation' + accreditation.id"
          :value="true"
          @click="handleYesOptionClicked"
        />
        <label :for="'Yes' + accreditation.id">Yes</label>
      </div>

      <div class="flex-1 space-x-4">
        <input
          :id="'No' + accreditation.id"
          v-model="hasAccreditation"
          type="radio"
          :name="'hasAccreditation' + accreditation.id"
          :value="false"
          @click="handleNoOptionClicked"
        />
        <label :for="'No' + accreditation.id">No</label>
      </div>
    </div>
    <div v-if="hasAccreditation" ref="form" class="mb-6 space-y-6">
      <section v-if="accreditation?.referenceIdRequired">
        <BaseInput
          v-model:input-value="formData.referenceId"
          label="Accreditation Number"
          identifier="referenceId"
          :is-valid="errorMessage.referenceId === ''"
          :disabled="isLoading"
        >
          <span v-if="errorMessage.referenceId" class="error m-1 !my-0 block">
            {{ errorMessage.referenceId }}
          </span>
        </BaseInput>
      </section>
      <section v-if="accreditation?.descriptionRequired">
        <BaseInput
          v-model:input-value="formData.description"
          label="Description"
          identifier="description"
          :is-valid="errorMessage.description === ''"
          :disabled="isLoading"
        >
          <span v-if="errorMessage.description" class="error m-1 !my-0 block">
            {{ errorMessage.description }}
          </span>
        </BaseInput>
      </section>
      <section
        v-if="accreditation?.validFromRequired"
        :id="'validFrom' + accreditation.id"
      >
        <VueDatePicker
          v-model="formData.validFrom"
          :enable-time-picker="false"
          placeholder="Valid From"
          format="dd-MM-yyyy"
          :max-date="new Date()"
          auto-apply
        />
        <span v-if="errorMessage.validFrom" class="error m-1 !my-0 block">
          {{ errorMessage.validFrom }}
        </span>
      </section>
      <section
        v-if="accreditation?.expiresAtRequired"
        :id="'expiresAt' + accreditation.id"
      >
        <VueDatePicker
          v-model="formData.expiresAt"
          :enable-time-picker="false"
          format="dd-MM-yyyy"
          placeholder="Expiry Date"
          :min-date="new Date()"
          :max-date="dayjs().add(10, 'year').toDate()"
          auto-apply
        />
        <span v-if="errorMessage.expiresAt" class="error m-1 !my-0 block">
          {{ errorMessage.expiresAt }}
        </span>
      </section>
      <section v-if="accreditation?.documentRequired">
        <label class="mb-2 block" :for="'document' + accreditation.id">
          Accreditation Document
        </label>
        <a
          v-if="documentSource"
          class="mb-2 block truncate text-blue-600 underline"
          data-test="accreditation-document-link"
          target="_blank"
          rel="noopener noreferrer"
          :href="documentSource"
        >
          {{ documentLabel }}
        </a>
        <span
          v-if="accreditationDocument?.message"
          class="mb-2 block text-sm text-gray-500"
          data-test="accreditation-document-status"
        >
          {{ accreditationDocument.message }}
        </span>
        <input
          :id="'document' + accreditation.id"
          accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png"
          :disabled="isLoading"
          name="document"
          type="file"
          @change="documentPicked"
        />
      </section>
    </div>
    <AlertBanner
      v-else-if="hasAccreditation === false && isMandatory"
      class="mb-6"
      component-style="warning"
      content="This site requires you to have this accreditation."
    />
    <span
      :data-test="
        accreditation?.name.toLowerCase().replace(' ', '-') +
        '-acknowledgment-statement'
      "
    >
      By submitting this form, you confirm that the information provided is
      correct.
    </span>
  </section>
</template>

<script setup lang="ts">
  import { computed, PropType, reactive, ref, toRef, watch } from 'vue'
  import BaseInput from '@/components/BaseInput.vue'
  import {
    AccreditationType,
    fetchAccreditationDocument,
  } from '@/services/api/accreditation'
  import { refusedContentStatus } from '@/services/api/documentContent'
  import ReportableException from '@/exceptions/ReportableException'
  import AlertBanner from '@/components/AlertBanner.vue'
  import dayjs from 'dayjs'
  import customParseFormat from 'dayjs/plugin/customParseFormat'
  import type { FormDocument } from '@verifiedit/form-generator'
  import Log from '@/utilities/Log'
  import objectUrls from '@/components/use/objectUrls'
  import { defaultDocumentStatus } from '@/services/api/site'
  import { explainDocument } from '@/types/documents'
  import { useUserStore } from '@/storage/user'
  import VueDatePicker from '@vuepic/vue-datepicker'
  import '@vuepic/vue-datepicker/dist/main.css'

  dayjs.extend(customParseFormat)

  const props = defineProps({
    accreditation: {
      required: true,
      type: Object as PropType<AccreditationType>,
    },
    isMandatory: Boolean,
    shouldValidate: Boolean,
  })

  const emit = defineEmits([
    'canSave',
    'hasAccreditation',
    'doesntHaveAccreditation',
    'validated',
  ])

  const formData = reactive({
    description: '',
    expiresAt: '',
    referenceId: '',
    validFrom: '',
  })

  const errorMessage = reactive({
    description: '',
    expiresAt: '',
    referenceId: '',
    validFrom: '',
  })

  const form = ref<HTMLDivElement | null>(null)
  const mandatoryFields = ref<string[]>([])
  const accreditation = toRef(props, 'accreditation')
  const isLoading = ref(false)
  const hasAccreditation = ref<boolean | null>(null)

  /**
   * The accreditation's document. Deliberately not a field of `formData`: `getMandatoryFields`
   * derives its list from `<field>Required` flags matched against that object, and the api
   * validates the document as `nullable` — an accreditation that already has one must not be
   * made to pick it again to save an edit.
   */
  const accreditationDocument = ref<FormDocument | null>(null)
  const pickedDocument = ref<File | null>(null)
  const { releaseObjectUrl, trackObjectUrl } = objectUrls()

  /**
   * Whether there are bytes to show is a question about what this app holds, never about the
   * document's status: a file the user has just picked is readable from the copy the browser
   * already has while its scan has not even begun.
   */
  const documentSource = computed(
    () =>
      accreditationDocument.value?.content ??
      accreditationDocument.value?.preview ??
      null,
  )

  /**
   * A document already on the accreditation has no name to show — there is no filename column
   * to publish one and nothing is derived from the accreditation's own fields (B54/B56) — so it
   * is labelled for what it is. A file just picked is named by the user's own choice of file.
   */
  const documentLabel = computed(
    () => accreditationDocument.value?.name ?? 'View document',
  )

  void restoreDocument()

  watch(
    () => formData.referenceId,
    (change) => {
      manageErrorMessage(change, 'referenceId')
    },
  )

  watch(
    () => formData.description,
    (change) => {
      manageErrorMessage(change, 'description')
    },
  )

  watch(
    () => formData.validFrom,
    (change) => {
      manageErrorMessage(change, 'validFrom')
    },
  )

  watch(
    () => formData.expiresAt,
    (change) => {
      manageErrorMessage(change, 'expiresAt')
    },
  )

  watch(
    () => props.shouldValidate,
    () => {
      if (props.shouldValidate) {
        validate()
      }
    },
  )

  watch(hasAccreditation, (hasAccreditation) => {
    if (hasAccreditation) {
      emit('hasAccreditation', accreditation.value?.id)
    } else {
      emit('doesntHaveAccreditation', accreditation.value?.id)
    }
  })

  /**
   * The document already on the accreditation, if there is one. Its status is what decides
   * whether its content is asked for at all — the same order every other document surface in
   * this app follows — and a content response that contradicts an `available` status is
   * reported as the fault it is and shown as a document with nothing behind it (B90).
   */
  async function restoreDocument(): Promise<void> {
    const userAccreditation = props.accreditation?.userAccreditations?.[0]

    if (userAccreditation === undefined || !userAccreditation.document) {
      return
    }

    const restored: FormDocument = {
      name: null,
      status: defaultDocumentStatus(userAccreditation.document.status),
      uuid: userAccreditation.document.uuid,
    }

    if (restored.status === 'available') {
      try {
        const { content, mimeType } = await fetchAccreditationDocument(
          userAccreditation.userId,
          userAccreditation.id,
        )

        restored.content = trackObjectUrl(content)
        restored.mimeType = mimeType
      } catch (error) {
        Log.notify(
          error as Error,
          'Accreditation document content contradicted its status',
        )

        restored.status = refusedContentStatus(error)
      }
    }

    accreditationDocument.value = explainDocument(restored)
  }

  function documentPicked(event: Event): void {
    const picked = (event.target as HTMLInputElement).files?.[0]

    if (picked === undefined) {
      return
    }

    // A second pick supersedes the first, and takes the url that was made for it with it.
    releaseDocumentSource()

    pickedDocument.value = picked
    accreditationDocument.value = explainDocument({
      mimeType: picked.type,
      name: picked.name,
      // Nothing has been uploaded yet — the file goes out with the accreditation when it is
      // saved — so this is the copy the browser already holds, and the wording is what the api
      // will report for it the moment it arrives: quarantined until its scan clears.
      preview: trackObjectUrl(URL.createObjectURL(picked)),
      status: 'awaiting_scan',
      // The api mints the uuid when it stores the file. Nothing on this surface keys on one, so
      // there is nothing to put here until a save reports it.
      uuid: '',
    })
  }

  function releaseDocumentSource(): void {
    if (documentSource.value !== null) {
      releaseObjectUrl(documentSource.value)
    }
  }

  function manageErrorMessage(data: string | null, fieldName: string) {
    if (data !== '' && data !== null) {
      errorMessage[fieldName] = ''
      return
    }

    errorMessage[fieldName] = 'Field cannot be empty'
  }

  function getMandatoryFields() {
    Object.entries(props.accreditation)
      .map(([fieldName, visibility]) => [
        fieldName.replace('Required', ''),
        visibility,
      ])
      .filter(
        ([fieldName, visibility]) =>
          visibility &&
          Object.prototype.hasOwnProperty.call(formData, fieldName as string),
      )
      .map(([fieldName]) => mandatoryFields.value.push(fieldName as string))

    mandatoryFields.value = [...new Set(mandatoryFields.value)]
  }

  function validate(): boolean {
    if (hasAccreditation.value === false) {
      emit('validated')
      return false
    }

    getMandatoryFields()

    mandatoryFields.value.forEach((field) => {
      manageErrorMessage(formData[field], field)
    })

    const isValid = Object.values(errorMessage).every(
      (message) => message === '',
    )

    emit('validated')

    emit('canSave', {
      canSave: isValid,
      userAccreditation: isValid ? acquireData() : null,
    })

    return isValid
  }

  function acquireData() {
    if (accreditation.value?.id === undefined) {
      throw new ReportableException('Missing accreditation data', {
        accreditation: accreditation.value,
      })
    }

    return createUserAccreditationRequestData()
  }

  function createUserAccreditationRequestData() {
    const data = {
      accreditationId: accreditation.value.id,
      accreditationName: accreditation.value.name,
      description: formData.description,
      expiresAt: formData.expiresAt
        ? dayjs(formData.expiresAt).endOf('day').format('DD-MM-YYYY HH:mm:ss')
        : '',
      referenceId: formData.referenceId,
      userId: useUserStore().id,
      validFrom: formData.validFrom,
    }

    if (accreditation.value?.userAccreditations[0] !== undefined) {
      data['userAccreditationId'] =
        accreditation.value?.userAccreditations[0].id
    }

    // The file itself, sent with the accreditation rather than uploaded on its own. Left out
    // entirely when nothing was picked, so saving an edit cannot replace a document with
    // nothing.
    if (pickedDocument.value !== null) {
      data['document'] = pickedDocument.value
    }

    return data
  }

  function handleYesOptionClicked() {
    hasAccreditation.value = true
  }

  function handleNoOptionClicked() {
    hasAccreditation.value = false
  }
</script>
